// Google Ads and mail pages merged into tabbed pages (2026-10). A stored permission for a merged page
// grants its host page, so nobody loses access; the permissions dialog shows only host keys.
export const PAGE_KEY_SUCCESSORS: Record<string, string> = {
  'google-ads-url-health': 'google-ads-anomalies',
  'google-ads-extensions': 'google-ads-ads',
  'google-ads-ai-reports': 'google-ads-ai-analysis',
  'mail-aliases': 'mail-accounts',
  'mail-logs': 'mail-queue',
  'mail-spam': 'mail-queue',
  'mail-packages': 'mail-allocations',
  'mail-cloudflare': 'mail-bayi-quotas',
};

/** allowed_pages with the host key added for every merged-page key it contains (null = full access) */
export function normalizeAllowedPages(pages: string[] | null | undefined): string[] | null | undefined {
  if (!Array.isArray(pages)) return pages;
  const out = new Set(pages);
  pages.forEach(p => { const host = PAGE_KEY_SUCCESSORS[p]; if (host) out.add(host); });
  return Array.from(out);
}

/** Same, with the merged-page keys removed (for editing permissions) */
export function currentPageKeys(pages: string[] | null | undefined): string[] | null {
  const n = normalizeAllowedPages(pages);
  return Array.isArray(n) ? n.filter(p => !PAGE_KEY_SUCCESSORS[p]) : null;
}

// WhatsApp pages were merged into tabbed pages too, but their tabs were separate permissions, so
// they stay separate: each hub path has a virtual key that no user stores, granted when the user
// may open any of its tabs, and the hub shows only the tabs the user may open.
export const HUB_PAGE_KEYS: Record<string, string[]> = {
  'waha-conversations': ['waha-support-tickets', 'waha-department-messages', 'waha-all-messages', 'waha-mentions', 'waha-support-tickets-trash'],
  'waha-contacts-hub': ['waha-contacts', 'waha-groups'],
  'waha-automation': ['waha-welcome-messages', 'waha-quick-replies', 'waha-chatbot-settings', 'waha-chatbot-rules', 'waha-chatbot-flows'],
  // The AI assistant page: its own permission, also open to whoever may manage the chatbot
  'waha-ai-assistant': ['waha-chatbot-settings'],
  'templates-hub': ['templates', 'templates-submission'],
  'whatsapp-web-hub': ['whatsapp-web', 'whatsapp-web-contacts'],
  'users-hub': ['users'],
};

/** Whether allowed_pages (null = everything) grants a page key, including a hub's virtual key */
export function hasPageKey(allowed: string[] | null | undefined, key: string): boolean {
  if (!Array.isArray(allowed)) return true;
  return allowed.includes(key) || (HUB_PAGE_KEYS[key] || []).some(k => allowed.includes(k));
}
